/**
 * Route copy for `src/routes/analytics` (keys: `routes.analytics.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  aiInsightsPage: {
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    high: 'High',
    low: 'Low',
    couldNotLoad: 'Could not load.',
    aiTemporarilyUnavailable: 'AI temporarily unavailable',
    upstreamUpstream: 'upstream: {{upstream}}',
    tryAgain: 'Try again',
    noData: 'No data',
    anErrorOccurred: 'An error occurred.',
    labelHHScoreScoreSample: '{{label}} {{h}}h · score {{score}} · {{sample_size}} post(s)',
    labelHHNoData: '{{label}} {{h}}h · no data',
    vAssessed: '{{v}} assessed',
    heuristicModelNotTrained: 'heuristic (model not trained)',
    trainedModel: 'trained model',
    risky: 'risky',
    noRiskyItemDetected: 'No risky item detected.',
    trained: 'Trained',
    notTrained: 'Not trained',
    algorithm: 'Algorithm',
    accuracy: 'Accuracy',
    auc: 'AUC',
    samples: 'Samples',
    trainedOn: 'Trained on',
    modelNotTrainedPredictionsUseA: 'Model not trained — predictions use a transparent heuristic until there is enough real CRM data.',
    training: 'Training…',
    retrain: 'Retrain',
    train: 'Train',
    engagementRate: 'Engagement rate (%)',
    numberOfFollowers: 'Number of followers',
    responseRate01: 'Response rate (0-1)',
    pastConversions: 'Past conversions',
    recencyDays: 'Recency (days)',
    budget: 'Budget',
    analysisNotPossible: 'Analysis not possible.',
    enterAtLeastOneNumericFeature: 'Enter at least one numeric feature.',
    scoringNotPossible: 'Scoring not possible.',
    bestTimeProvidedHistory: 'Best time (provided history)',
    whatIfNoConnection: 'What-if — no connection',
    postHistory: 'Post history',
    onePerLineDateEngagementLength: 'One per line: “date[, engagement]”. {{length}} valid.',
    platform: 'Platform',
    analyzing: 'Analyzing…',
    analyze: 'Analyze',
    vPostSTimezone: '{{v}} post(s) · {{timezone}}',
    noSlotProvideADatedHistory: 'No slot — provide a dated history.',
    scoreScore: 'score {{score}}',
    leadScoreDeterministic: 'Lead score (deterministic)',
    explainableOneOff: 'Explainable, one-off',
    computing: 'Computing…',
    score: 'Score',
    modelTrainedOnVSampleS: 'Model trained on {{v}} sample(s).',
    notEnoughDataToTrainA: 'Not enough data to train a real model.',
    trainingNotPossible: 'Training not possible.',
    aiInsights: 'AI insights',
    predictionsScoringWinBestTimeAnd: 'Predictions (scoring, win, best time) and social listening — real data, honest states.',
    all: 'All',
    period: 'Period',
    days: 'days',
    refresh: 'Refresh',
    predictiveModels: 'Predictive models',
    realRegistryTrainedOnYourCrm: 'Real registry — trained on your CRM data',
    loadingTemplates: 'Loading templates…',
    leadScore: 'Lead score',
    winProbabilityDeals: 'Win probability (deals)',
    leadScoreDistribution: 'Lead score distribution',
    noScoringDataYet: 'No scoring data yet',
    connectCrmContactsWithInteractionsTo: 'Connect CRM contacts with interactions to generate scores.',
    contacts: 'Contacts',
    dealWinProbability: 'Deal win probability',
    noDealsToAssessYet: 'No deals to assess yet',
    addDealsOpenAndClosedTo: 'Add deals (open and closed) to estimate win probabilities.',
    deals: 'Deals',
    bestTimeToPublish: 'Best time to publish',
    vPostSConfidenceV2Timezone: '{{v}} post(s) · confidence {{v2}} · {{timezone}}',
    insufficientPublishingHistory: 'Insufficient publishing history',
    publishRegularlyToGetSlotsBased: 'Publish regularly to get slots based on your real data.',
    hashtagTrends: 'Hashtag trends',
    noTrendTracked: 'No trend tracked',
    noHashtagMetricIngestedInThe: 'No hashtag metric ingested in the period — ingest some to track their trends.',
    mentionS: 'mention(s)',
    new: 'new',
    engagementRate2: 'Engagement rate',
    overallVInteractionsPost: 'Overall {{v}} interactions / post',
    noEngagementDataYet: 'No engagement data yet',
    publishAndCollectMetricsToCompute: 'Publish and collect metrics to compute the engagement rate.',
    interactionsReceivedPerPostNoReal: 'Interactions received per post (no real impressions available).',
    sentimentOverTime: 'Sentiment over time',
    trendOverall: 'Trend: {{overall}}',
    noSentimentAnalysisYet: 'No sentiment analysis yet',
    sentimentIsComputedOnSocialInteractions: 'Sentiment is computed on social interactions with AI (LiteLLM).',
    netSentiment100VeryNegative100: 'Net sentiment (−100 very negative → +100 very positive) per day.',
    medium: 'Medium',
  },
  analyticsLayout: {
    performance: 'Performance',
    monitoring: 'Monitoring',
    advanced: 'Advanced',
    analyticsIntelligence: 'Analytics & Intelligence',
    performancePredictiveAiInsightsHashtagsC: 'Performance, predictive AI insights, hashtags, competitive monitoring, influencers, funnels, retention, attribution and goals.',
    analyticsNavigation: 'Analytics navigation',
    advancedAnalytics: 'Advanced analytics',
  },
  analyticsOverviewPage: {
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    lengthSkippedMissingKey: '{{length}} skipped (missing key)',
    snapshotCapturedV: 'Snapshot captured: {{v}}.',
    captureNotPossible: 'Capture not possible.',
    adKpisUnavailable: 'Ad KPIs unavailable.',
    slotsUnavailable: 'Slots unavailable.',
    audienceGrowthUnavailable: 'Audience growth unavailable.',
    leadingPlatformV: 'Leading platform: {{v}}',
    biggestContributorToEngagementOverThe: 'Biggest contributor to engagement over the last {{days}} days.',
    vBestEngagementRate: '{{v}}: best engagement rate',
    vOverV2PostS: '{{v}} over {{v2}} post(s).',
    volumeVPublishesTheMost: 'Volume: {{v}} publishes the most',
    vPostSInThePeriod: '{{v}} post(s) in the period.',
    audienceRisingV: 'Audience rising ({{v}})',
    audienceFallingV: 'Audience falling ({{v}})',
    fromLabelToLabel2: 'From {{label}} to {{label2}}.',
    bestSlotVHourH: 'Best slot: {{v}} ~{{hour}}h',
    slotWithTheHighestPerformanceScore: 'Slot with the highest performance score (prediction).',
    topPostV: 'Top post: {{v}}',
    platformPost: '{{platform}} post',
    summary: '# Summary',
    topPosts: '# Top posts',
    content: 'Content',
    platform: 'Platform',
    like: 'Like',
    shares: 'Shares',
    scope: 'Scope',
    impressions: 'Impressions',
    rate: 'Rate',
    analysisRecommendationsContent: 'Analysis & recommendations — content',
    overview: 'Overview',
    consolidatedPerformanceOverDaysDaysEngag: 'Consolidated performance over {{days}} days — engagement, audience and best posts.',
    period: 'Period',
    days: 'days',
    capturing: 'Capturing…',
    captureASnapshot: 'Capture a snapshot',
    export: 'Export',
    details: 'Details',
    technicalDetails: 'Technical details',
    originAndFreshnessOfTheData: 'Origin and freshness of the data per block — useful to audit the reports.',
    noSummary: 'No summary',
    posts: 'Posts',
    lastPeriodDaysDays: 'Last {{period_days}} days',
    totalEngagement: 'Total engagement',
    engagementRate: 'Engagement rate',
    connectedAccounts: 'Connected accounts',
    connectAPageForReach: 'Connect a page for reach',
    connectAPageForImpressions: 'Connect a page for impressions',
    followerGrowth: 'Follower growth',
    leadingV: 'Leading: {{v}}',
    connectAPageForTheAudience: 'Connect a page for the audience',
    adPerformance: 'Ad performance',
    adSpend: 'Ad spend',
    paidCampaignsLastDaysDays: 'Paid campaigns · last {{days}} days',
    roas: 'ROAS',
    attributedRevenueSpend: 'Attributed revenue / spend',
    awaitingConversions: 'Awaiting conversions',
    engagementByPlatform: 'Engagement by platform',
    noEngagementDataYet: 'No engagement data yet',
    audienceGrowth: 'Audience growth',
    allPlatforms: 'All platforms',
    noAudienceDataYet: 'No audience data yet',
    connectAPageMetaAndCapture: 'Connect a page (Meta) and capture a snapshot to track followers.',
    postBreakdown: 'Post breakdown',
    noPostPerPlatform: 'No post per platform',
    bestPublishingSlots: 'Best publishing slots',
    noPredictedSlot: 'No predicted slot',
    theModelNeedsADatedPublishing: 'The model needs a dated publishing history to recommend slots.',
    weekdaysCHScoreV: '{{WEEKDAYS}} {{c}}h · score {{v}}',
    bestPerformingPosts: 'Best-performing posts',
    noPostAnalyzed: 'No post analyzed',
    exportTheOverview: 'Export the overview',
  },
  attributionPage: {
    firstTouch: 'First touch',
    lastTouch: 'Last touch',
    linear: 'Linear',
    uShaped402040: 'U-shaped (40/20/40)',
    timeDecay: 'Time decay',
    dataDrivenMarkov: 'Data-driven (Markov)',
    channel: 'Channel',
    campaign: 'Campaign',
    source: 'Source',
    support: 'Support',
    touchpoints: 'Touchpoints',
    conversions: 'Conversions',
    spend: 'Spend',
    dataUnavailable: 'Data unavailable.',
    rawAttributionData: 'Raw attribution data',
    noData: 'No data',
    importAttributionDataToFeedThe: 'Import attribution data to feed the report and these views.',
    subjectSubjectId: 'subject {{subject_id}}',
    attributionModel: 'Attribution model',
    groupBy: 'Group by',
    attributionReportUnavailable: 'Attribution report unavailable.',
    unattributed: '(unattributed)',
    topRevenueV: 'Top revenue: {{v}}',
    vAttributedV2Conversions: '{{v}} attributed · {{v2}} conversions.',
    bestRoiVV2: 'Best ROI: {{v}} ({{v2}})',
    roasVForASpendOf: 'ROAS {{v}} for a spend of {{v2}}.',
    negativeRoiVV2: 'Negative ROI: {{v}} ({{v2}})',
    spendHigherThanAttributedRevenueTo: 'Spend higher than attributed revenue — to optimize.',
    vOfConversionsUnattributed: '{{v}}% of conversions unattributed',
    addTouchpointsToImproveTheModel: 'Add touchpoints to improve the model\'s coverage.',
    revenue: 'Revenue',
    spend2: 'Spend',
    attributionRoi: 'Attribution & ROI',
    attributeConversionsAndRevenueToThe: 'Attribute conversions and revenue to the channels/campaigns that preceded them, then measure the ROI.',
    rawData: 'Raw data',
    importData: 'Import data',
    export: 'Export',
    attributionWindowDays: 'Attribution window (days)',
    fromOptional: 'From (optional)',
    toOptional: 'To (optional)',
    includeTheRoiSpend: 'Include the ROI (spend)',
    computing: 'Computing…',
    compute: 'Compute',
    computingTheReport: 'Computing the report…',
    overallRoi: 'Overall ROI',
    totalRevenue: 'Total revenue',
    totalSpend: 'Total spend',
    overallRoas: 'Overall ROAS',
    unattributed2: 'Unattributed',
    unattributedRevenue: 'Unattributed revenue',
    breakdownByGroupByLabel: 'Breakdown by {{GROUP_BY_LABEL}}',
    noAttributedConversionTouchpointsFillIn: 'No attributed conversion. Touchpoints fill in automatically from real activity (CRM contacts); add your conversions and spend to feed this report.',
    shareOfAttributedRevenue: 'Share of attributed revenue',
    exportTheAttribution: 'Export the attribution',
    attributionModelGroupbyCsv: 'attribution-{{model}}-{{groupBy}}.csv',
    importAttributionData: 'Import attribution data',
  },
  audiencePage: {
    noDataForThisDimension: 'No data for this dimension.',
    ingestedSnapshotSSaved: '{{ingested}} snapshot(s) saved.',
    savingFailed: 'Saving failed.',
    enterImportDemographics: 'Enter / import demographics',
    automaticMetaIgCaptureIsBlocked: 'Automatic Meta/IG capture is blocked by a credential + upstream endpoint dependency (owner-gated). Enter real demographics here (e.g. from the Meta Insights export). Each dimension is a list',
    bracketCount: 'bracket=count',
    onePerLine: '(one per line).',
    accountKey: 'Account key',
    stableAccountIdentifierEGIg: 'Stable account identifier (e.g. ig:myaccount).',
    igBrand: 'ig:brand',
    platform: 'Platform',
    accountName: 'Account name',
    brand: '@brand',
    numberOfFollowers: 'Number of followers',
    gender: 'Gender',
    eGFemale60Male40: 'e.g. female=60, male=40',
    female60Male40: 'female=60\nmale=40',
    age: 'Age',
    country: 'Country',
    eGFr70Ca20: 'e.g. FR=70, CA=20',
    fr70Ca20: 'FR=70\nCA=20',
    cities: 'Cities',
    eGParis30Montreal15: 'e.g. Paris=30, Montreal=15',
    paris30Montreal15: 'Paris=30\nMontreal=15',
    languages: 'Languages',
    eGFr80En20: 'e.g. fr=80, en=20',
    fr80En20: 'fr=80\nen=20',
    saving: 'Saving…',
    saveTheSnapshot: 'Save the snapshot',
    demographicsUnavailable: 'Demographics unavailable.',
    audience: 'Audience',
    followerDemographicsAgeGenderCountriesCi: 'Follower demographics (age/gender, countries, cities, languages) per connected account — real, persisted data. No distribution is made up.',
    allPlatforms: 'All platforms',
    hideTheEntryForm: 'Hide the entry form',
    enterDemographics: 'Enter demographics',
    accounts: 'Accounts',
    followersTotal: 'Followers (total)',
    platforms: 'Platforms',
    distinctCountries: 'Distinct countries',
    noDemographicsSaved: 'No demographics saved',
    enterOrImportDemographicsToShow: 'Enter or import demographics to show the follower breakdown.',
    ageGender: 'Age × gender',
    followersPerPlatform: 'Followers per platform',
    savedSnapshotsLength: 'Saved snapshots ({{length}})',
    date: 'Date',
    account: 'Account',
    followers: 'Followers',
    source: 'Source',
    normalizedSharePerDimensionVFor: 'Normalized share per dimension: {{v}} for the dominant gender bracket.',
  },
  bestTimeHeatmapPage: {
    heatmapUnavailable: 'Heatmap unavailable.',
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    postingFrequencyHistoryNoEngagementSnaps: 'Posting frequency (history) — no engagement snapshot yet',
    realEngagementPerPostMetricSnapshots: 'Real engagement per post (metric snapshots)',
    bestTimeToPublish: 'Best time to publish',
    fullDayHourGrid724: 'Full day × hour grid (7 × 24). Intensity reflects the real engagement per slot, or — without a snapshot — the real posting frequency.',
    allPlatforms: 'All platforms',
    days: 'days',
    postsAnalyzed: 'Posts analyzed',
    peakEngagement: 'Peak engagement',
    bestSlot: 'Best slot',
    source: 'Source',
    noData: 'No data',
    noPostPublishedNorEngagementSnapshot: 'No post published nor engagement snapshot in the period — nothing to map.',
    intensityByDayHour: 'Intensity by day × hour',
    darkerCellsConcentrateTheMostEngagement: 'Darker cells concentrate the most engagement (or posts). Hover a cell for the real detail.',
    optimalSlotVAtV2: 'Optimal slot: {{v}} at {{v2}}',
    thisSlotGatheredVEngagementOver: 'This slot gathered {{v}} engagement over {{posts}}',
    postSThePeakOfThe: 'post(s) — the peak of the analyzed period.',
  },
  brandingPage: {
    yourBrand: 'Your brand',
    customReportFooter: 'Custom report footer',
    logo: 'Logo',
    analyticsReport: 'Analytics report',
    trend: 'Trend',
    couldNotLoadTheBrand: 'Could not load the brand.',
    invalidColorUseAHexLike: 'Invalid color — use a hex like #4f46e5.',
    brandSaved: 'Brand saved.',
    couldNotSaveWritePermissionsRequired: 'Could not save (write permissions required?).',
    brandReset: 'Brand reset.',
    couldNotReset: 'Could not reset.',
    reportBranding: 'Report branding',
    customizeTheLookOfYourAnalytics: 'Customize the look of your analytics reports (logo, colors, footer, sender). Applied to every rendered or exported report.',
    configured: 'Configured',
    notConfigured: 'Not configured',
    updated: 'Updated',
    readOnlyAWriteRoleIs: 'Read-only — a write role is required to edit the branding.',
    loadingTheBranding: 'Loading the branding…',
    brandCompanyName: 'Brand / company name',
    acmeAnalytics: 'Acme Analytics',
    logoUrl: 'Logo URL',
    shownInTheReportHeader: 'Shown in the report header',
    primaryColor: 'Primary color',
    hexEG4f46e5: 'Hex, e.g. #4f46e5',
    primaryColorPicker: 'Primary color picker',
    invalidHex: 'Invalid hex.',
    secondaryColor: 'Secondary color',
    hexEG0ea5e9: 'Hex, e.g. #0ea5e9',
    secondaryColorPicker: 'Secondary color picker',
    senderNameEmail: 'Sender name (email)',
    usedToSendTheReports: 'Used to send the reports',
    acmeReports: 'Acme Reports',
    customDomain: 'Custom domain',
    optionalForWhiteLabel: 'Optional — for white-label',
    reportsAcmeCom: 'reports.acme.com',
    footer: 'Footer',
    confidentialAcme2026: 'Confidential — © Acme 2026',
    saving: 'Saving…',
    saveTheBranding: 'Save the branding',
    reset: 'Reset',
    confirmTheReset: 'Confirm the reset?',
    preview: 'Preview',
    noBrandingConfigured: 'No branding configured',
    reportsUseTheDefaultLookAs: 'Reports use the default look as long as no branding is saved.',
  },
  charts: {
    noData: 'No data',
    timeCurve: 'Time curve',
    gauge: 'Gauge',
    breakdown: 'Breakdown',
  },
  checklistsPage: {
    french: 'French',
    inAppChecklists: 'In-app checklists',
  },
  cohortsPage: {
    day: 'Day',
    week: 'Week',
    month: 'Month',
    entryEvent: 'Entry event',
    returnEvent: 'Return event',
    granularity: 'Granularity',
    retentionAnalysisUnavailable: 'Retention analysis unavailable.',
    averageP1RetentionV: 'Average P1 retention: {{v}}%',
    averageFirstReturnAcrossLengthCohort: 'Average first return across {{length}} cohort(s).',
    bestCohortCohort: 'Best cohort: {{cohort}}',
    vP1RetentionV2Subjects: '{{v}} P1 retention ({{v2}} subjects).',
    mostFragileCohortCohort: 'Most fragile cohort: {{cohort}}',
    vP1RetentionToInvestigate: '{{v}} P1 retention — to investigate.',
    vSubjectsAnalyzed: '{{v}} subjects analyzed',
    entryCohortEventReturnReturnEvent: 'Entry “{{cohort_event}}” → return “{{return_event}}”, {{period}} granularity.',
    cohortRetention: 'Cohort retention',
    groupSubjectsByEntryPeriodAnd: 'Group subjects by entry period and track their return, period after period.',
    importEvents: 'Import events',
    export: 'Export',
    subjects: 'Subjects',
    cohorts: 'Cohorts',
    avgP1Retention: 'Avg. P1 retention',
    periods: 'Periods',
    definesTheCohort1stOccurrence: 'Defines the cohort (1st occurrence).',
    signup: 'signup',
    marksTheRetention: 'Marks the retention.',
    active: 'active',
    numberOfPeriods: 'Number of periods',
    fromOptional: 'From (optional)',
    toOptional: 'To (optional)',
    analyzing: 'Analyzing…',
    analyzeTheRetention: 'Analyze the retention',
    averageRetentionCurve: 'Average retention curve',
    notEnoughPeriodsToDrawA: 'Not enough periods to draw a curve.',
    retentionMatrix: 'Retention matrix',
    subjectSLengthCohortS: 'subject(s) · {{length}} cohort(s)',
    noCohort: 'No cohort',
    runAnAnalysis: 'Run an analysis',
    noSubjectEmittedTheEntryEvent: 'No subject emitted the entry event in the chosen period.',
    fillInTheEventsThenClick: 'Fill in the events, then click “Analyze the retention”.',
    cohort: 'Cohort',
    size: 'Size',
    vSubjectS: '{{v}} subject(s)',
    exportTheRetention: 'Export the retention',
    retentionVCsv: 'retention-{{v}}.csv',
    n1To52: '1 to 52.',
  },
  competitorBenchmark: {
    followers: 'Followers',
    posts: 'Posts',
    engagementRate: 'Engagement rate',
    averageEngagement: 'Average engagement',
    cadenceDaysBetweenPosts: 'Cadence (days between posts)',
    postsWeek: 'Posts / week',
    noData: 'No data',
    trendV: 'Trend — {{v}}',
    theCompetitorBenchmarkFailed: 'The competitor benchmark failed.',
    capturingTheSnapshotsFailed: 'Capturing the snapshots failed.',
    competitorBenchmark: 'Competitor benchmark',
    sideBySideComparisonTrendOver: 'Side-by-side comparison, trend over time and share of voice, from the saved snapshots (official Instagram / YouTube APIs only).',
    capturing: 'Capturing…',
    captureSnapshots: 'Capture snapshots',
    totalSnapshotSCaptured: '/{{total}} snapshot(s) captured.',
    notCaptured: 'not captured',
    loadingTheBenchmark: 'Loading the benchmark…',
    benchmarkUnavailable: 'Benchmark unavailable',
    accountSNotComparablePublicApi: 'account(s) not comparable — public API unavailable',
    platformNotSupported: 'platform not supported',
    account: 'Account',
    snapshots: 'Snapshots',
    notSupported: 'Not supported',
    leader: 'Leader',
    metric: 'Metric',
    period: 'Period',
    days: 'days',
    refreshing: 'Refreshing…',
    noSeriesYet: 'No series yet',
    noTrackedCompetitorHasAnOfficial: 'No tracked competitor has an official public API.',
    captureSnapshotsButtonAboveTheSeries: 'Capture snapshots (button above): the series builds with each capture.',
    shareOfVoice: 'Share of voice',
    cadenceIsNotAdditiveTheShare: 'Cadence is not additive: the share of voice is not computed for this metric.',
    asOfDate: 'As of {{date}} —',
    noDataCaptureSnapshotsToCompute: 'No data: capture snapshots to compute the share of voice.',
  },
  competitorsPage: {
    thisCompetitorMayAlreadyBeTracked: 'This competitor may already be tracked.',
    trackACompetitor: 'Track a competitor',
    platform: 'Platform',
    instagramYoutubeTiktok: 'instagram, youtube, tiktok…',
    handleIdentifier: 'Handle / identifier',
    brand: '@brand',
    displayName: 'Display name',
    optional: 'Optional',
    myBrand: 'My brand',
    cancel: 'Cancel',
    adding: 'Adding…',
    track: 'Track',
    theAnalysisFailed: 'The analysis failed.',
    analyzeV: 'Analyze {{v}}',
    analysisUnavailable: 'Analysis unavailable',
    channelNameKeyword: 'Channel name / keyword',
    channelName: 'Channel name',
    instagramUsername: 'Instagram username',
    postsToAnalyze: 'Posts to analyze',
    close: 'Close',
    analyzing: 'Analyzing…',
    analyze: 'Analyze',
    noRecentPostFound: 'No recent post found.',
    posts: 'Posts',
    cadenceD: 'Cadence (d)',
    avgViews: 'Avg. views',
    avgLikes: 'Avg. likes',
    topPost: 'Top post:',
    followers: 'Followers',
    avgEngagement: 'Avg. engagement',
    avgComm: 'Avg. comm.',
    postsTotal: 'Posts (total)',
    theComparisonFailed: 'The comparison failed.',
    compareTheCompetitors: 'Compare the competitors',
    comparisonUnavailable: 'Comparison unavailable',
    metrics: 'Metrics',
    commaSeparatedFollowersEngagementViews: 'Comma-separated (followers, engagement, views…)',
    followersEngagementViews: 'followers,engagement,views',
    includeMyBrand: 'Include my brand',
    windowDays: 'Window (days)',
    comparing: 'Comparing…',
    compare: 'Compare',
    noComparableMetricSyncAtLeast: 'No comparable metric — sync at least one competitor (“Sync”) to get real values.',
    totalV: 'Total {{v}}',
    leaderLeader: ' · leader: {{leader}}',
    noRealDataForThisMetric: 'No real data for this metric.',
    theSyncFailed: 'The sync failed.',
    mostMonitoredPlatformLabel: 'Most monitored platform: {{label}}',
    valueAccountSOutOfLength: '{{value}} account(s) out of {{length}} tracked · {{length2}} platform(s) covered.',
    noSyncedAccount: 'No synced account',
    syncedcountLengthAccountSSynced: '{{syncedCount}}/{{length}} account(s) synced',
    clickSyncOnACompetitorTo: 'Click “Sync” on a competitor to collect its metrics — Share of voice (“Tracked competitors” source) will then use them.',
    syncTheRemainingAccountsToInclude: 'Sync the remaining accounts to include them in the share of voice.',
    everyTrackedCompetitorHasSavedMetrics: 'Every tracked competitor has saved metrics, ready for the share of voice.',
    account: 'Account',
    savedMetrics: 'Saved metrics',
    noneSync: 'None — sync',
    sync: 'Sync',
    neverSynced: 'Never synced',
    actions: 'Actions',
    syncing: 'Syncing…',
    sync2: 'Sync',
    remove: 'Remove',
    confirm: 'Confirm?',
    competitors: 'Competitors',
    buildYourMonitoringPanelAndTrack: 'Build your monitoring panel and track coverage per platform.',
    export: 'Export',
    syncUnavailable: 'Sync unavailable',
    trackedCompetitors: 'Tracked competitors',
    platforms: 'Platforms',
    synced: 'Synced',
    vNeverSynced: '{{v}} never synced',
    syncCoverage: 'Sync coverage',
    breakdownByPlatform: 'Breakdown by platform',
    noTrackedCompetitor: 'No tracked competitor',
    addYourCompetitorsAccountsToSet: 'Add your competitors\' accounts to set up the monitoring.',
    exportTheCompetitors: 'Export the competitors',
    platform2: 'platform',
  },
  components: {
    tryAgain: 'Try again',
    loading: 'Loading…',
    upcomingFeature: 'Upcoming feature',
    thisSectionWillBeAvailableSoon: 'This section will be available soon — data is not collected for your account yet.',
    noData: 'No data',
    anErrorOccurred: 'An error occurred.',
    confirm: 'Confirm?',
    analysesRecommendations: 'Analyses & recommendations',
    notEnoughDataYetToDraw: 'Not enough data yet to draw analyses.',
    exportTheData: 'Export the data',
    previewOfTheCurrentlyLoadedData: 'Preview of the currently loaded data. The download saves exactly this content — no data is added or simulated.',
    noDataToExport: 'No data to export.',
    fileDownloaded: 'File downloaded.',
    close: 'Close',
    download: 'Download',
  },
  crmInsightsPage: {
    mon: 'Mon',
    tue: 'Tue',
    wed: 'Wed',
    thu: 'Thu',
    fri: 'Fri',
    sat: 'Sat',
    sun: 'Sun',
    cadenceInsightsUnavailable: 'Cadence insights unavailable.',
    mostActiveDayLabel: 'Most active day: {{label}}',
    vPostSInThePeriod: '{{v}} post(s) in the period.',
    dominantTimeSlotBesthourH: 'Dominant time slot: {{bestHour}}h',
    vPostSAroundThisHour: '{{v}} post(s) around this hour.',
    mainPlatformLabel: 'Main platform: {{label}}',
    vPostS: '{{v}} post(s).',
    postingCadence: 'Posting cadence',
    platform: 'Platform',
    all: 'All',
    windowDays: 'Window (days)',
    analyze: 'Analyze',
    noRealPostInThePeriod: 'No real post in the period',
    theAggregatesAreBuiltFromThe: 'The aggregates are built from the real publishing history. Publish content or widen the window.',
    posts: 'Posts',
    platforms: 'Platforms',
    activeDays: 'Active days',
    window: 'Window',
    byPlatform: 'By platform',
    byWeekday: 'By weekday',
    analytics: 'Analytics',
    byHour023: 'By hour (0–23)',
    posts2: 'Posts',
    ciHVPostS: '{{ci}}h · {{v}} post(s)',
    audienceInsightsUnavailable: 'Audience insights unavailable.',
    engagedpctEngagedContacts: '{{engagedPct}}% engaged contacts',
    vContactSWithAtLeast: '{{v}} contact(s) with at least one interaction.',
    quietpctSilentContacts: '{{quietPct}}% silent contacts',
    noInteractionForVDaysRe: 'No interaction for {{v}} days — re-engagement target.',
    audienceSegmentation: 'Audience & segmentation',
    silentThresholdDays: 'Silent threshold (days)',
    noCrmContact: 'No CRM contact',
    importOrCreateCrmContactsTo: 'Import or create CRM contacts to segment your audience.',
    contacts: 'Contacts',
    engaged: 'Engaged',
    silent: 'Silent',
    avgInteractions: 'Avg. interactions',
    byLifecycle: 'By lifecycle',
    bySource: 'By source',
    crmInsights: 'CRM insights',
    postingCadenceAndAudienceSegmentationCom: 'Posting cadence and audience segmentation, computed on your real data (publishing history + CRM contacts). Server-side pandas rollups — never simulated data.',
  },
  funnelsPage: {
    couldNotCreate: 'Could not create.',
    newFunnel: 'New funnel',
    name: 'Name',
    acquisition: 'Acquisition',
    stepsOrderedEvents: 'Steps (ordered events)',
    onePerLineOrCommaSeparated: 'One per line (or comma-separated). At least 2. E.g.: signup, activate, purchase.',
    pageViewSignupActivatePurchase: 'page_view\nsignup\nactivate\npurchase',
    cancel: 'Cancel',
    creating: 'Creating…',
    create: 'Create',
    theAdHocAnalysisFailed: 'The ad-hoc analysis failed.',
    adHocAnalysis: 'Ad-hoc analysis',
    onePerLineOrCommaSeparated2: 'One per line (or comma-separated). At least 2. Analysis without saving a funnel.',
    pageViewSignupPurchase: 'page_view\nsignup\npurchase',
    windowH: 'Window (h)',
    from: 'From',
    to: 'To',
    analyzing: 'Analyzing…',
    analyze: 'Analyze',
    noEventForTheseStepsIn: 'No event for these steps in the period.',
    inputs: 'Inputs',
    conversions: 'Conversions',
    conversion: 'Conversion',
    averageTime: 'Average time',
    eventsUnavailable: 'Events unavailable.',
    ingestedEvents: 'Ingested events',
    eventName: 'Event name',
    all: 'All',
    subjectId: 'Subject (id)',
    filter: 'Filter',
    noEvents: 'No events',
    importEventsToFeedTheFunnels: 'Import events to feed the funnels and cohorts.',
    subjectSubjectId: 'subject {{subject_id}}',
    theFunnelAnalysisFailed: 'The funnel analysis failed.',
    largestDropOffAtStepV: 'Largest drop-off at step {{v}} · {{event_name}}',
    vSubjectSLostSinceThe: '−{{v}} subject(s) lost since the previous step ({{v2}} drop-off).',
    bestTransitionEventName: 'Best transition: {{event_name}}',
    vMoveOnFromThePrevious: '{{v}} move on from the previous step.',
    overallConversionV: 'Overall conversion: {{v}}',
    vConversionsOutOfV2Entries: '{{v}} conversions out of {{v2}} entries{{v3}}.',
    analysisName: 'Analysis · {{name}}',
    export: 'Export',
    noEventForTheseSteps: 'No event for these steps',
    funnelsFillInAutomaticallyFromReal: 'Funnels fill in automatically from real activity (CRM, posts). Use “Import events” to seed or complete the data matching these steps.',
    overallConversion: 'Overall conversion',
    steps: 'Steps',
    totalDropOff: 'Total drop-off',
    window: 'Window',
    pointsOfAttention: 'Points of attention',
    exportName: 'Export · {{name}}',
    funnelVCsv: 'funnel-{{v}}.csv',
    funnelsUnavailable: 'Funnels unavailable.',
    couldNotDelete: 'Could not delete.',
    conversionFunnels: 'Conversion funnels',
    measureConversionAndDropOffPoints: 'Measure conversion and drop-off points step by step on your real events.',
    events: 'Events',
    importEvents: 'Import events',
    noFunnel: 'No funnel',
    defineASequenceOfStepsEvents: 'Define a sequence of steps (events) to measure conversion.',
    stepS: 'step(s)',
    delete: 'Delete',
    confirm: 'Confirm?',
    selectAFunnelToRunThe: 'Select a funnel to run the analysis.',
    couldNotSave: 'Could not save.',
    editTheFunnelName: 'Edit funnel · {{name}}',
    editTheFunnelName2: 'Edit funnel {{name}}',
    saving: 'Saving…',
    save: 'Save',
    edit: 'Edit',
  },
  goalsPage: {
    objective: 'Objective',
    achieved: 'Achieved',
    onTrack: 'On track',
    risky: 'Risky',
    behind: 'Behind',
    missed: 'Missed',
    couldNotCreate: 'Could not create.',
    newObjective: 'New objective',
    name: 'Name',
    followerGrowth: 'Follower growth',
    metric: 'Metric',
    followers: 'followers',
    type: 'Type',
    direction: 'Direction',
    increase: 'Increase',
    decrease: 'Decrease',
    startingValue: 'Starting value',
    targetValue: 'Target value',
    currentValue: 'Current value',
    periodStart: 'Period start',
    periodEnd: 'Period end',
    cancel: 'Cancel',
    creating: 'Creating…',
    create: 'Create',
    couldNotEdit: 'Could not edit.',
    editName: 'Edit “{{name}}”',
    saving: 'Saving…',
    save: 'Save',
    progressUnavailable: 'Progress unavailable.',
    couldNotLoadTheHistory: 'Could not load the history.',
    measurementNotPossible: 'Measurement not possible.',
    targetV: 'target {{v}}',
    edit: 'Edit',
    delete: 'Delete',
    confirm: 'Confirm?',
    progress: 'Progress…',
    expectedPaceV: 'Expected pace: {{v}}',
    paceV: 'Pace: {{v}}',
    projectedV: 'Projected: {{v}}',
    remainingV: 'Remaining: {{v}}',
    newMeasurement: 'New measurement…',
    recordAMeasurementForName: 'Record a measurement for {{name}}',
    hide: 'Hide',
    history: 'History',
    loadingTheHistory: 'Loading the history…',
    tryAgain: 'Try again',
    noMeasurementRecordedYet: 'No measurement recorded yet.',
    measurementHistoryLength: 'Measurement history ({{length}})',
    objectivesUnavailable: 'Objectives unavailable.',
    couldNotDelete: 'Could not delete.',
    leadingName: 'Leading: {{name}}',
    vOfTheTargetMetric: '{{v}} of the target ({{metric}}).',
    laggingName: 'Lagging: {{name}}',
    vOfTheTargetPotentialPriority: '{{v}} of the target — potential priority.',
    nextDeadlineName: 'Next deadline: {{name}}',
    periodEndsOnV: 'Period ends on {{v}}.',
    objectivesKpis: 'Objectives & KPIs',
    trackProgressTowardYourTargetsThe: 'Track progress toward your targets, the expected pace and the end-of-period projection.',
    export: 'Export',
    objectives: 'Objectives',
    targetsReachedEst: 'Targets reached (est.)',
    avgProgressEst: 'Avg. progress (est.)',
    averageProgressEstimated: 'Average progress (estimated)',
    noObjective: 'No objective',
    defineAKpiOrAnOkr: 'Define a KPI or an OKR with a target and a period to track its progress.',
    exportTheObjectives: 'Export the objectives',
    current: 'current',
  },
  hashtagsPage: {
    couldNotCreateTheWatchlist: 'Could not create the watchlist.',
    newWatchlist: 'New watchlist',
    name: 'Name',
    eGFashionCompetitors: 'E.g. Fashion competitors',
    hashtags: 'Hashtags',
    separatedBySpacesOrCommasThe: 'Separated by spaces or commas. The # is optional.',
    ootdStreetwearSneakers: '#ootd streetwear sneakers',
    cancel: 'Cancel',
    create: 'Create',
    couldNotAddMaybeAlreadyPresent: 'Could not add (maybe already present).',
    addAHashtag: 'Add a hashtag…',
    addAHashtag2: 'Add a hashtag',
    delete: 'Delete',
    confirm: 'Confirm?',
    noHashtags: 'No hashtags.',
    viewTheSeries: 'View the series',
    removeHashtag: 'Remove #{{hashtag}}',
    engagement: 'Engagement',
    averageEngagement: 'Average engagement',
    scope: 'Scope',
    impressions: 'Impressions',
    posts: 'Posts',
    hashtagPerformanceUnavailable: 'Hashtag performance unavailable.',
    performanceOfYourHashtagsOutgoing: 'Performance of your hashtags (outgoing)',
    rankingOfTheHashtagsInYour: 'Ranking of the hashtags in your OWN posts by the real engagement/reach earned.',
    sortBy: 'Sort by',
    period: 'Period',
    loading: 'Loading…',
    noPostMetricRecordedYetV: 'No post metric recorded yet ({{v}} post(s) scanned). Run a metric snapshot capture to feed this ranking.',
    postsScanned: 'posts scanned',
    withHashtag: 'with hashtag',
    distinctHashtags: 'distinct hashtags',
    hashtag: 'Hashtag',
    avgPost: 'Avg./post',
    platforms: 'Platforms',
    seriesUnavailable: 'Series unavailable.',
    addAtLeastOneHashtagTo: 'Add at least one hashtag to a watchlist before collecting.',
    collectedPointSCollectedCreatedCreated: '{{collected}} point(s) collected ({{created}} created, {{updated}} updated).',
    theCollectionFailed: 'The collection failed.',
    monitorHashtagsPerWatchlistAndTrack: 'Monitor hashtags per watchlist and track their real metrics over time.',
    collecting: 'Collecting…',
    collect: 'Collect',
    watchlists: 'Watchlists',
    trackedHashtags: 'Tracked hashtags',
    instagramQuota: 'Instagram quota',
    trackedcountIgQuotaOver7D: '{{trackedCount}} / {{IG_QUOTA}} over 7 d',
    hashtagTrend: '#{{hashtag}} trend',
    seriesTrend: 'Series trend',
    trackedHashtagsInstagramBusinessQuota: 'Tracked hashtags (Instagram Business quota)',
    automaticCollectionInstagramYoutubeIsLim: 'Automatic collection (Instagram/YouTube) is limited to 30 hashtags per 7-day period.',
    automaticCollectionComing: 'Automatic collection coming',
    noWatchlist: 'No watchlist',
    createAWatchlistToGroupThe: 'Create a watchlist to group the hashtags to monitor.',
    hashtagSeries: '#{{hashtag}} series',
    metricSeries: 'Metric series',
    allPlatforms: 'All platforms',
    selectAHashtagToShowIts: 'Select a hashtag to show its real metric series.',
    noDataIngested: 'No data ingested',
    noMetricPointHasBeenRecorded: 'No metric point has been recorded for this hashtag yet.',
    pointS: 'point(s)',
    averageEngagementLatest: 'Average engagement · latest:',
    peakV: 'Peak: {{v}}',
    risingTrendV: 'Rising trend ({{v}})',
    fallingTrendV: 'Falling trend ({{v}})',
    fromLabelToLabel2Over: 'From {{label}} to {{label2}}, over',
    realMeasurementS: 'real measurement(s).',
  },
  importDataModal: {
    events: 'Events',
    productMarketingEventsSubjectTrackedIden: 'Product/marketing events (subject = tracked identity) that feed the funnels and cohorts.',
    subjectId: 'Subject (id)',
    contact42CookieAbc: 'contact-42 / cookie-abc',
    eventName: 'Event name',
    occurredOn: 'Occurred on',
    projectOptional: 'Project (optional)',
    touchpoints: 'Touchpoints',
    aSubjectSMarketingTouchesBefore: 'A subject\'s marketing touches before their conversion (channel, campaign).',
    channel: 'Channel',
    emailAdsLinkedin: 'email / ads / linkedin',
    campaignOptional: 'Campaign (optional)',
    sourceOptional: 'Source (optional)',
    conversions: 'Conversions',
    conversionsRevenueAttributedByGoingBack: 'Conversions (revenue) attributed by going back over the same subject\'s touchpoints.',
    revenue: 'Revenue',
    typeOptional: 'Type (optional)',
    purchaseSubscription: 'purchase / subscription',
    spend: 'Spend',
    spendIncurredPerCampaignChannelJoined: 'Spend incurred per campaign/channel, joined for ROI/ROAS.',
    campaign: 'Campaign',
    amount: 'Amount',
    channelOptional: 'Channel (optional)',
    currency: 'Currency',
    periodStartOptional: 'Period start (optional)',
    periodEndOptional: 'Period end (optional)',
    sentiment: 'Sentiment',
    preScoredSentimentPointsSubjectLabel: 'Pre-scored sentiment points (subject + label + score) that feed the sentiment time series.',
    topic: 'Topic',
    myBrandProductX: 'my brand / product-x',
    groupsThePointsForTheTime: 'Groups the points for the time series',
    label: 'Label',
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    score: 'Score',
    between1NegativeAnd1Positive: 'Between -1 (negative) and 1 (positive)',
    platformOptional: 'Platform (optional)',
    textReferenceOptional: 'Text reference (optional)',
    comment123: 'comment #123',
    inboxEvents: 'Inbox events',
    conversationLifecycleEventsReceipt1stRep: 'Conversation lifecycle events (receipt / 1st reply / resolution) that feed the inbox performance.',
    conversationId: 'Conversation (id)',
    receivedOn: 'Received on',
    agentOptional: 'Agent (optional)',
    emailDmWhatsapp: 'email / dm / whatsapp',
    n1stReplyOnOptional: '1st reply on (optional)',
    resolvedOnOptional: 'Resolved on (optional)',
    importEvents: 'Import events',
    rowVTheLabelFieldIs: 'Row {{v}}: the “{{label}}” field is required (or invalid).',
    fillInAtLeastOneRow: 'Fill in at least one row.',
    ingestedLabelImported: '{{ingested}} {{label}} imported.',
    theImportFailed: 'The import failed.',
    lastRecordedPoints: 'Last recorded points',
    rowV: 'Row {{v}}',
    remove: 'Remove',
    addALine: '+ Add a line',
    close: 'Close',
    importing: 'Importing…',
    importV: 'Import {{v}}',
    cad: 'CAD',
  },
  inboxPerformancePage: {
    byAgent: 'agent',
    byChannel: 'channel',
    mMinRsS: '{{m}} min {{rs}} s',
    hHRmMin: '{{h}} h {{rm}} min',
    unassigned: '(unassigned)',
    eventsUnavailable: 'Events unavailable.',
    ingestedInboxEvents: 'Ingested inbox events',
    agentId: 'Agent (id)',
    all: 'All',
    loading: 'Loading…',
    filter: 'Filter',
    loadingEvents: 'Loading events…',
    noInboxEvent: 'No inbox event',
    importConversationEventsToFeedThe: 'Import conversation events to feed the inbox performance.',
    receivedV: 'received {{v}}',
    resolvedV: ' · resolved {{v}}',
    n1stReplV: ' · 1st repl. {{v}}',
    couldNotLoadTheInboxPerformance: 'Could not load the inbox performance.',
    processed: 'Processed',
    answered: 'Answered',
    resolved: 'Resolved',
    replyRate: 'Reply rate',
    resolutionRate: 'Resolution rate',
    n1stReplyAvg: '1st reply (avg.)',
    resolutionAvg: 'Resolution (avg.)',
    inboxTeamPerformance: 'Inbox / team performance',
    firstReplyTimeResolutionRateAnd: 'First reply time, resolution rate and volume per agent or per channel, computed on the ingested conversation events. No data is simulated.',
    rawEvents: 'Raw events',
    importEvents: 'Import events',
    refreshing: 'Refreshing…',
    refresh: 'Refresh',
    groupBy: 'Group by',
    agent: 'Agent',
    channel: 'Channel',
    since: 'Since',
    optional: 'Optional',
    until: 'Until',
    loadingInboxPerformance: 'Loading inbox performance…',
    noInboxPerformanceData: 'No inbox performance data',
    theIndicatorsAreComputedFromConversation: 'The indicators are computed from conversation events (receipt, first reply, resolution). No event has been ingested for your account in this period yet.',
    conversationsHandled: 'Conversations handled',
    vAnsweredV2Resolved: '{{v}} answered · {{v2}} resolved',
    avgResolutionV: 'Avg. resolution {{v}}',
    volumeBy: 'Volume by',
    noGroupToShow: 'No group to show.',
    detailByKeyheader: 'Detail by {{keyHeader}}',
    noGroupInThisPeriod: 'No group in this period.',
    importInboxEvents: 'Import inbox events',
  },
  influencersPage: {
    yes: 'yes',
    no: 'no',
    couldNotCreate: 'Could not create.',
    newInfluencer: 'New influencer',
    displayName: 'Display name',
    topics: 'Topics',
    commaSeparated: 'Comma-separated',
    fashionLifestyle: 'fashion, lifestyle',
    notes: 'Notes',
    optional: 'Optional',
    cancel: 'Cancel',
    creating: 'Creating…',
    create: 'Create',
    importedImportedSkippedSkipped: '{{imported}} imported, {{skipped}} skipped.',
    couldNotImport: 'Could not import.',
    importInfluencersCsv: 'Import influencers (CSV)',
    csvContent: 'CSV content',
    headerRequiredReservedColumnsDisplayName: 'Header required. Reserved columns: display_name (required), contact_ref, notes, topics. Other columns = per-platform handles.',
    displayNameTopicsInstagramNotesJane: 'display_name,topics,instagram,notes\nJane Doe,fashion;lifestyle,@janedoe,VIP',
    close: 'Close',
    importing: 'Importing…',
    import: 'Import',
    noProfileFoundForThisSearch: 'No profile found for this search.',
    discoveryFailed: 'Discovery failed.',
    addingFailed: 'Adding failed.',
    discoverInfluencers: 'Discover influencers',
    discoveryUnavailable: 'Discovery unavailable',
    platform: 'Platform',
    keywordSearch: 'Keyword search',
    fitnessCooking: 'fitness, cooking…',
    instagramUsername: 'Instagram username',
    numberOfResults: 'Number of results',
    searching: 'Searching…',
    search: 'Search',
    followersFollowers: ' · {{followers}} followers',
    added: 'Added',
    add: 'Add',
    scoreUnavailable: 'Score unavailable.',
    couldNotAddToTheCrm: 'Could not add to the CRM.',
    couldNotRemoveFromTheCrm: 'Could not remove from the CRM.',
    couldNotDelete: 'Could not delete.',
    sourceSource: 'Source: {{source}}',
    inTheCrm: '✓ In the CRM',
    removeFromTheCrm: 'Remove from the CRM',
    confirm: 'Confirm?',
    adding: 'Adding…',
    addToTheCrm: 'Add to the CRM',
    computingTheScore: 'Computing the score…',
    influenceScore: 'Influence score',
    unavailable: 'unavailable',
    topBestPlatform: 'Top: {{best_platform}}',
    hideTheScore: 'Hide the score',
    score: 'Score',
    delete: 'Delete',
    deleteCrmContact: 'Delete + CRM contact?',
    incrmLengthLinkedToTheCrm: '{{inCrm}}/{{length}} linked to the CRM',
    turnTheKeyProfilesIntoCrm: 'Turn the key profiles into CRM contacts to manage the relationship.',
    everyInfluencerIsLinkedToThe: 'Every influencer is linked to the CRM.',
    dominantTopicLabel: 'Dominant topic: {{label}}',
    valueInfluencerSUniquetopicsTopicS: '{{value}} influencer(s) · {{uniqueTopics}} topic(s) in total.',
    influencers: 'Influencers',
    buildYourInfluencerBaseLinkThem: 'Build your influencer base, link them to the CRM and track the topics.',
    export: 'Export',
    discover: 'Discover',
    importCsv: 'Import CSV',
    inTheCrm2: 'In the CRM',
    vCoverage: '{{v}}% coverage',
    topic1: 'Topic #1',
    valueProfileS: '{{value}} profile(s)',
    mostFollowedTopics: 'Most followed topics',
    topics2: 'topics',
    searchByName: 'Search by name…',
    searchAnInfluencer: 'Search an influencer',
    noInfluencer: 'No influencer',
    createOrImportProfilesToBuild: 'Create or import profiles to build your influencer base.',
    exportTheInfluencers: 'Export the influencers',
  },
  installPage: {
    view: 'View',
  },
  intelDashboardsPage: {
    metric: 'Metric',
    chart: 'Chart',
    table: 'Table',
    none: 'None',
    today: 'Today',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    currentMonth: 'Current month',
    currentYear: 'Current year',
    postsSummary: 'Posts (summary)',
    averageEngagement: 'Average engagement',
    engagementRatePlatform: 'Engagement rate / platform',
    topPosts: 'Top posts',
    adSpendAds: 'Ad spend (ads)',
    overallRoasAds: 'Overall ROAS (ads)',
    reach: 'Reach',
    partial: 'Partial',
    empty: 'Empty',
    error: 'Error',
    couldNotCreate: 'Could not create.',
    newDashboard: 'New dashboard',
    name: 'Name',
    socialPerformance: 'Social performance',
    descriptionOptional: 'Description (optional)',
    cancel: 'Cancel',
    creating: 'Creating…',
    create: 'Create',
    couldNotRename: 'Could not rename.',
    renameTheDashboard: 'Rename the dashboard',
    saving: 'Saving…',
    rename: 'Rename',
    theParametersMustBeValidJson: 'The parameters must be valid JSON, e.g. {"platform":"linkedin"}.',
    couldNotAddTheWidget: 'Could not add the widget.',
    addAWidget: 'Add a widget',
    title: 'Title',
    posts: 'Posts',
    type: 'Type',
    sourcePreset: 'Source preset',
    seedsTheServiceEndpointTowardA: 'Seeds the service/endpoint toward a real analytics endpoint',
    custom: '— Custom —',
    service: 'Service',
    analytics: 'analytics',
    method: 'Method',
    get: 'GET',
    post: 'POST',
    endpoint: 'Endpoint',
    gatewayPathEGApiV1: 'Gateway path, e.g. /api/v1/analytics/summary',
    dateWindow: 'Date window',
    mergedIntoTheParametersOnResolution: 'Merged into the parameters on resolution',
    fieldSelect: 'Field (select)',
    dottedPathToExtractEG: 'Dotted path to extract, e.g. total_posts',
    parametersJson: 'Parameters (JSON)',
    optionalEGPlatformLinkedin: 'Optional, e.g. {"platform":"linkedin"}',
    adding: 'Adding…',
    add: 'Add',
    couldNotEditTheWidget: 'Could not edit the widget.',
    editTheWidget: 'Edit the widget',
    save: 'Save',
    widgetResolutionFailed: 'Widget resolution failed.',
    noData: 'No data.',
    widgetsUnavailable: 'Widgets unavailable.',
    dashboardResolutionFailed: 'Dashboard resolution failed.',
    couldNotDeleteTheWidget: 'Could not delete the widget.',
    resolveLive: 'Resolve (live)',
    delete: 'Delete',
    deleteTheDashboard: 'Delete the dashboard?',
    noWidget: 'No widget',
    addAWidgetPointingItTo: 'Add a widget pointing it to a real analytics endpoint; it will be resolved live.',
    thisDashboardHasNoWidgetYet: 'This dashboard has no widget yet.',
    resolvingLive: 'Resolving live…',
    untitled: '(untitled)',
    clickResolveLive: 'Click “Resolve (live)”.',
    dashboardsUnavailable: 'Dashboards unavailable.',
    couldNotDelete: 'Could not delete.',
    couldNotRefreshTheDashboard: 'Could not refresh the dashboard.',
    dashboards: 'Dashboards',
    composeDashboardsInWhichEachWidget: 'Compose dashboards in which each widget is resolved live from your real analytics endpoints. A widget\'s failure is isolated — never fabricated data.',
    noDashboard: 'No dashboard',
    createOneToGetStarted: 'Create one to get started.',
    noDashboardYet: 'No dashboard yet.',
    widgetS: 'widget(s)',
    selectADashboard: 'Select a dashboard.',
  },
  monitoringModel: {
    anomalyMethodSensitivitySensitivity: 'Anomaly · {{method}} · sensitivity {{sensitivity}}',
    valueVV2: 'Value {{v}} {{v2}}',
    nameTheAlert: 'Name the alert.',
    enterTheMonitoredMetric: 'Enter the monitored metric.',
    enterANumericThreshold: 'Enter a numeric threshold.',
    theSensitivityMustBeAPositive: 'The sensitivity must be a positive number.',
    theSourceWindowMustBeBetween: 'The source window must be between 2 and 90 days.',
    enterTheSourceHashtagToMonitor: 'Enter the source hashtag to monitor.',
    enterTheListeningMonitorId: 'Enter the listening monitor ID.',
    nameTheDigest: 'Name the digest.',
    provideAnRruleEGFreq: 'Provide an RRULE (e.g. FREQ=WEEKLY;BYDAY=MO).',
    theRruleMustContainFreqE: 'The RRULE must contain FREQ= (e.g. FREQ=DAILY).',
    everyDayAt8Am: 'Every day at 8 am',
    everyMondayAt9Am: 'Every Monday at 9 am',
    firstOfTheMonthAt8: 'First of the month at 8 am',
  },
  monitoringMonitoringPage: {
    active: 'Active',
    paused: 'Paused',
    alertName: 'Alert name',
    monitoredMetric: 'Monitored metric',
    enterAtLeastOneNumericValue: 'Enter at least one numeric value.',
    detectionFailed: 'Detection failed.',
    couldNotLoadTheAlerts: 'Could not load the alerts.',
    alertCreated: 'Alert created.',
    couldNotCreateTheAlert: 'Could not create the alert.',
    alertPaused: 'Alert paused.',
    alertEnabled: 'Alert enabled.',
    couldNotUpdate: 'Could not update.',
    alertDeleted: 'Alert deleted.',
    couldNotDelete: 'Could not delete.',
    triggeredTriggerSaved: 'Triggered — trigger saved.',
    conditionTriggered: 'Condition triggered.',
    conditionNotTriggered: 'Condition not triggered.',
    couldNotSubmitTheReview: 'Could not submit the review.',
    alertTriggeredOnTheRealData: 'Alert triggered on the real data.',
    checkedNotTriggered: 'Checked — not triggered.',
    checkFailed: 'Check failed.',
    couldNotLoadTheTriggers: 'Could not load the triggers.',
    couldNotLoadTheDigests: 'Could not load the digests.',
    digestScheduled: 'Digest scheduled.',
    couldNotCreateTheDigest: 'Could not create the digest.',
    digestPaused: 'Digest paused.',
    digestEnabled: 'Digest enabled.',
    digestDeleted: 'Digest deleted.',
    digestSent: 'Digest sent.',
    digestComposedNotSent: 'Digest composed (not sent).',
    couldNotRun: 'Could not run.',
    monitoring: 'Monitoring',
    metricAlertsAnomalyDetectionAndScheduled: 'Metric alerts, anomaly detection and scheduled digests (RRULE). Write actions are reserved for editor/administrator roles.',
    yourRoleIsReadOnlyCreating: 'Your role is read-only: creating/editing is reserved for editor or administrator roles. You can view alerts and digests and run a local anomaly detection.',
    anomalyDetection: 'Anomaly detection',
    pasteANumericSeriesTheService: 'Paste a numeric series; the service spots the outliers (robust z-score / IQR). No data is kept.',
    numericSeries: 'Numeric series',
    separatedByCommasOrSpaces: 'Separated by commas or spaces.',
    method: 'Method',
    robustZScore: 'Robust z-score',
    iqr: 'IQR',
    sensitivity: 'Sensitivity',
    lowerMoreSensitive: 'Lower = more sensitive.',
    analyzing: 'Analyzing…',
    detect: 'Detect',
    sensitivitySensitivity: 'sensitivity {{sensitivity}}',
    anomaly: 'anomaly',
    lengthPoints: '· {{length}} points',
    noOutlierDetectedInThisSeries: 'No outlier detected in this series.',
    scoreScore: ' (score {{score}})',
    metricAlerts: 'Metric alerts',
    defineAConditionThresholdOrAnomaly: 'Define a condition (threshold or anomaly) on a metric; evaluate it against a series and keep the trigger history.',
    newAlert: 'New alert',
    engagementDrop: 'Engagement drop',
    conditionType: 'Condition type',
    threshold: 'Threshold',
    anomaly2: 'Anomaly',
    operator: 'Operator',
    greaterThan: '> greater than',
    greaterThanOrEqual: '≥ greater than or equal',
    lessThan: '< less than',
    lessThanOrEqual: '≤ less than or equal',
    equals: '= equals',
    dataSourceForCheckNow: 'Data source (for “Check now”)',
    linkARealSourceSoThe: 'Link a real source so the live check resolves a real value. Without a source, only manual evaluation of a series is possible.',
    source: 'Source',
    noneManualEvaluation: 'None (manual evaluation)',
    hashtagMentions: 'Hashtag mentions',
    mentionsListening: 'Mentions (listening)',
    netSentiment: 'Net sentiment',
    hashtag: 'Hashtag',
    launch: '#launch',
    platformOptional: 'Platform (optional)',
    instagram: 'instagram',
    monitorId: 'Monitor ID',
    topicOptional: 'Topic (optional)',
    brandProduct: 'brand, product…',
    windowDays: 'Window (days)',
    activeUponCreation: 'Active upon creation',
    creating: 'Creating…',
    createTheAlert: 'Create the alert',
    loadingAlerts: 'Loading alerts…',
    noAlert: 'No alert',
    createYourFirstAlertAbove: 'Create your first alert above.',
    checking: 'Checking…',
    checkNow: 'Check now',
    review: 'Review',
    triggers: 'Triggers',
    pause: 'Pause',
    enable: 'Enable',
    delete: 'Delete',
    confirm: 'Confirm?',
    sourceUnavailable: 'Source unavailable',
    triggered: 'Triggered',
    notTriggered: 'Not triggered',
    valueValue: '· value {{value}}',
    webhookSent: '· webhook sent',
    scheduledDigests: 'Scheduled digests',
    aRecapSentOnAnRrule: 'A recap sent on an RRULE recurrence. Run it on demand to test the composition and sending.',
    newDigest: 'New digest',
    digestName: 'Digest name',
    weeklyPerformanceRecap: 'Weekly performance recap',
    recipients: 'Recipients',
    commaSeparatedEmailsOptional: 'Comma-separated emails (optional).',
    recurrenceRrule: 'Recurrence (RRULE)',
    eGFreqWeeklyBydayMo: 'E.g. FREQ=WEEKLY;BYDAY=MO;BYHOUR=9',
    schedule: 'Schedule',
    loadingDigests: 'Loading digests…',
    noDigest: 'No digest',
    scheduleYourFirstDigestAbove: 'Schedule your first digest above.',
    lengthRecipientS: '· {{length}} recipient(s)',
    lastAutoSend: '· last auto send',
    run: 'Run',
    sent: 'Sent',
    composedNotSent: 'Composed (not sent)',
    lengthSourceS: '· {{length}} source(s)',
    evaluateName: 'Evaluate · {{name}}',
    condition: 'Condition:',
    over: 'over',
    seriesToEvaluate: 'Series to evaluate',
    commaSeparatedNumericValues: 'Comma-separated numeric values.',
    saveATriggerIfTheCondition: 'Save a trigger if the condition is met',
    conditionTriggered2: 'Condition triggered',
    conditionNotTriggered2: 'Condition not triggered',
    triggerSavedTriggerId: 'Trigger saved: {{trigger_id}}',
    close: 'Close',
    evaluating: 'Evaluating…',
    triggersName: 'Triggers · {{name}}',
    loading: 'Loading…',
    noTrigger: 'No trigger',
    thisAlertHasNeverBeenTriggered: 'This alert has never been triggered yet.',
    valueValue2: 'value {{value}}',
    n2To90: '2 to 90',
  },
  peopleWatchlistsPage: {
    averageEngagement: 'Average engagement',
    followers: 'Followers',
    posts: 'Posts',
    couldNotCreateTheList: 'Could not create the list.',
    newAccountList: 'New account list',
    name: 'Name',
    eGDirectCompetitors: 'E.g. Direct competitors',
    accounts: 'Accounts',
    separatedBySpacesOrCommasThe: 'Separated by spaces or commas. The @ is optional.',
    nikeAdidasUnderarmour: '@nike adidas @underarmour',
    cancel: 'Cancel',
    create: 'Create',
    couldNotAddMaybeAlreadyPresent: 'Could not add (maybe already present).',
    addAnAccount: 'Add an account…',
    addAnAccount2: 'Add an account',
    delete: 'Delete',
    confirm: 'Confirm?',
    noAccounts: 'No accounts.',
    viewTheSeries: 'View the series',
    removeHandle: 'Remove @{{handle}}',
    couldNotSaveThePoint: 'Could not save the point.',
    addAPointHandle: 'Add a point — @{{handle}}',
    platform: 'Platform',
    date: 'Date',
    eG12000: 'E.g. 12000',
    eG8: 'E.g. 8',
    avgEngagement: 'Avg. engagement',
    eG34: 'E.g. 3.4',
    aRealPointReSavableFor: 'A real point, re-savable for the same date (upsert). Enter at least one measurement.',
    saving: 'Saving…',
    saveThePoint: 'Save the point',
    couldNotLoadTheTrackedAccounts: 'Could not load the tracked accounts.',
    couldNotDelete: 'Could not delete.',
    couldNotRemove: 'Could not remove.',
    seriesUnavailable: 'Series unavailable.',
    addAtLeastOneAccountTo: 'Add at least one account to a list before collecting.',
    collectedPointSCollectedCreatedCreated: '{{collected}} point(s) collected ({{created}} created, {{updated}} updated).',
    theCollectionFailed: 'The collection failed.',
    automaticCollectionInstagramBusinessDisc: 'Automatic collection (Instagram business_discovery / YouTube) requires API keys (YOUTUBE_API_KEY / IG_GRAPH_TOKEN).',
    followedAccounts: 'Followed accounts',
    monitorAccountsCompetitorsPerListAnd: 'Monitor accounts/competitors per list and track their real metrics (followers, posts, engagement) over time.',
    collecting: 'Collecting…',
    collect: 'Collect',
    newList: 'New list',
    lists: 'Lists',
    handleFollowers: '@{{handle}} followers',
    lastReading: 'Last reading',
    selectAnAccount: 'Select an account',
    metriclabelTrend: '{{metricLabel}} trend',
    seriesTrend: 'Series trend',
    automaticCollectionComing: 'Automatic collection coming',
    noAccountList: 'No account list',
    createAListToGroupThe: 'Create a list to group the accounts to monitor.',
    noTrackedAccountYet: 'No tracked account yet.',
    handleSeries: '@{{handle}} series',
    metricSeries: 'Metric series',
    metric: 'Metric',
    allPlatforms: 'All platforms',
    addAPoint: 'Add a point',
    selectAnAccountToShowIts: 'Select an account to show its real metric series.',
    noDataIngested: 'No data ingested',
    noPointRecordedYetAddA: 'No point recorded yet. Add a manual point or run a collection.',
    noMetricPointHasBeenRecorded: 'No metric point has been recorded for this account yet.',
    vPointS: '{{v}} point(s)',
    latestV: '· latest: {{v}}',
    peakV: 'Peak: {{v}}',
    risingTrendV: 'Rising trend ({{v}})',
    fallingTrendV: 'Falling trend ({{v}})',
    fromLabelToLabel2OverLength: 'From {{label}} to {{label2}}, over {{length}} real “{{metricLabel}}” measurement(s).',
    automaticAccountCollection: 'Automatic account collection',
    active: 'Active',
    toConfigure: 'To configure',
  },
  reportsPage: {
    yes: 'yes',
    no: 'no',
    watchlistSummary: 'Watchlist summary',
    trackedHashtagWatchlists: 'Tracked hashtag watchlists',
    trackedHashtags: 'Tracked hashtags',
    trackedHashtagsWatchlists: 'Tracked hashtags (watchlists)',
    influencerProfiles: 'Influencer profiles',
    trackedInfluencerProfiles: 'Tracked influencer profiles',
    competitorAccounts: 'Competitor accounts',
    trackedCompetitorAccounts: 'Tracked competitor accounts',
    bestSlotsInstagram: 'Best slots (Instagram)',
    bestPublishingSlotsInstagram: 'Best publishing slots (Instagram)',
    aiSummaryNarrativeBilledInCredits: 'AI summary (narrative) — billed in credits',
    executiveSummaryAi: 'Executive summary (AI)',
    sumsUpThisReportSKey: 'Sums up this report\'s key indicators and proposes 2 to 3 concrete, actionable recommendations.',
    manualNone: 'Manual (none)',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    couldNotCreate: 'Could not create.',
    newReport: 'New report',
    name: 'Name',
    monthlyReport: 'Monthly report',
    blocks: 'Blocks',
    scheduling: 'Scheduling',
    recipients: 'Recipients',
    commaSeparatedEmailsOptional: 'Comma-separated emails (optional)',
    ceoExampleComCmoExampleCom: 'ceo@example.com, cmo@example.com',
    cancel: 'Cancel',
    creating: 'Creating…',
    create: 'Create',
    couldNotSave: 'Could not save.',
    editName: 'Edit “{{name}}”',
    description: 'Description',
    optional: 'Optional',
    whatThisReportIsFor: 'What this report is for…',
    lengthCustomBlockSKept: '+ {{length}} custom block(s) kept.',
    saving: 'Saving…',
    save: 'Save',
    frequency: 'Frequency',
    format: 'Format',
    schedulesUnavailable: 'Schedules unavailable.',
    actionNotPossible: 'Action not possible.',
    couldNotDelete: 'Could not delete.',
    couldNotSchedule: 'Could not schedule.',
    schedulesName: 'Schedules — “{{name}}”',
    noSchedule: 'No schedule',
    addAScheduleToDistributeThis: 'Add a schedule to distribute this report automatically.',
    active: 'Active',
    paused: 'Paused',
    next: 'Next:',
    recipientsV: 'Recipients: {{v}}',
    noRecipientDownloadOnly: 'No recipient (download only)',
    pause: 'Pause',
    resume: 'Resume',
    edit: 'Edit',
    delete: 'Delete',
    confirm: 'Confirm?',
    newSchedule: 'New schedule',
    schedule: 'Schedule',
    addASchedule: 'Add a schedule',
    close: 'Close',
    n7Days: '7 days',
    n14Days: '14 days',
    n30Days: '30 days',
    n90Days: '90 days',
    n12Weeks: '12 weeks',
    allPlatforms: 'All platforms',
    twitterX: 'Twitter / X',
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
    socialMonitoringReport: 'Social monitoring report',
    rangeDaysDayWindow: '{{range_days}}-day window',
    generatedOn: 'Generated on',
    followedAccounts: 'Followed accounts',
    spikesDetected: 'Spikes detected',
    sentiment: 'Sentiment',
    topHashtagsMentions: 'Top hashtags (mentions)',
    noAggregatedMentionInThePeriod: 'No aggregated mention in the period.',
    trackedAccountsFollowers: 'Tracked accounts (followers)',
    vPosts: '{{v}} posts',
    noTrackedAccountAggregatedInThe: 'No tracked account aggregated in the period.',
    zZScoreVAvgV2: 'z={{z_score}} · {{v}} (avg. {{v2}})',
    noSpikeNotEnoughPointsMin: 'No spike: not enough points (min. 3) or no variation above the threshold (z-score ≥ 2) in the period.',
    noSentimentSignalInThePeriod: 'No sentiment signal in the period.',
    reachImpressions: 'Reach / impressions',
    unavailableForTheSakeOfHonesty: 'Unavailable for the sake of honesty',
    noCollectedPlatformProvidesRealImpressio: 'No collected platform provides real impressions/reach.',
    metricsUnavailable: 'Metrics unavailable',
    listsUnavailable: 'Lists unavailable.',
    selectAtLeastOneWatchlistOr: 'Select at least one watchlist or an ad-hoc topic.',
    nameTheReport: 'Name the report.',
    couldNotCreateTheReport: 'Could not create the report.',
    newMonitoringReport: 'New monitoring report',
    hashtagWatchlists: 'Hashtag watchlists',
    noHashtagWatchlist: 'No hashtag watchlist.',
    trackedAccountLists: 'Tracked account lists',
    noAccountList: 'No account list.',
    adHocHashtags: 'Ad-hoc hashtags',
    optionalInAdditionToTheWatchlists: 'Optional — in addition to the watchlists',
    ootdStreetwear: '#ootd streetwear',
    adHocAccounts: 'Ad-hoc accounts',
    optionalInAdditionToTheLists: 'Optional — in addition to the lists',
    nikeAdidas: '@nike adidas',
    platform: 'Platform',
    window: 'Window',
    reportName: 'Report name',
    requiredToSaveThePreviewDoes: 'Required to save (the preview does not need it)',
    competitorMonitoringMonthly: 'Competitor monitoring — monthly',
    whatThisMonitoringReportIsFor: 'What this monitoring report is for…',
    yourRoleIsReadOnlyThe: 'Your role is read-only: the preview stays available, but saving the report is reserved for editors.',
    preview: 'Preview',
    createTheReport: 'Create the report',
    monitoringReportUnavailable: 'Monitoring report unavailable.',
    monitoringReportSaved: 'Monitoring report saved.',
    reportsUnavailable: 'Reports unavailable.',
    historyUnavailable: 'History unavailable.',
    executionFailed: 'Execution failed.',
    downloadNotPossible: 'Download not possible.',
    scheduledcountLengthReportSScheduled: '{{scheduledCount}}/{{length}} report(s) scheduled',
    noScheduledReport: 'No scheduled report',
    scheduleTheRecurringReportsToAutomate: 'Schedule the recurring reports to automate their distribution.',
    everyReportIsAutomated: 'Every report is automated.',
    lastRunStatus: 'Last run: {{status}}',
    sched: 'sched.',
    actions: 'Actions',
    run: 'Run',
    schedules: 'Schedules',
    viewTheSchedules: 'View the schedules',
    date: 'Date',
    status: 'Status',
    delivery: 'Delivery',
    sent: 'Sent',
    notDeliveredCount: 'Not delivered · {{count}}',
    file: 'File',
    downloading: 'Downloading…',
    downloadFormat: 'Download {{format}}',
    reports: 'Reports',
    composeReportsFromBlocksScheduleThem: 'Compose reports from blocks, schedule them and track their runs.',
    export: 'Export',
    socialMonitoring: 'Social monitoring',
    noMonitoringReport: 'No monitoring report',
    chooseWatchlistsThenRunAPreview: 'Choose watchlists, then run a preview.',
    scheduled: 'Scheduled',
    runs: 'Runs',
    succeeded: 'Succeeded',
    runsByStatus: 'Runs by status',
    runs2: 'runs',
    reportTemplates: 'Report templates',
    noReport: 'No report',
    composeAReportFromBlocksWatchlists: 'Compose a report from blocks (watchlists, influencers, competitors…).',
    runHistory: 'Run history',
    noRun: 'No run',
    exportTheReports: 'Export the reports',
  },
  sentimentPage: {
    perDay: 'day',
    perWeek: 'week',
    perMonth: 'month',
    positive: 'Positive',
    negative: 'Negative',
    neutral: 'Neutral',
    aiServiceUnavailableTheLlmGateway: 'AI service unavailable: the LLM gateway is not configured or is unreachable. Try again later.',
    theClassificationFailed: 'The classification failed.',
    sentimentAnalysis: 'Sentiment analysis',
    analyzePersist: 'Analyze & persist',
    aggregatedPreview: 'Aggregated preview',
    classifiesEachTextAndByDefault: 'Classifies each text and (by default) saves the points to feed the time series below.',
    classifiesABatchAndReturnsAn: 'Classifies a batch and returns an aggregated net sentiment index, without persistence.',
    readOnlyRoleClassificationUsesAi: 'Read-only role: classification uses AI credits and is reserved for authorized roles.',
    textsToClassify: 'Texts to classify',
    oneTextPerLineMaxMax: 'One text per line. Max {{max}}. {{length}} entered{{v}}.',
    greatServiceIRecommendItDisappointed: 'Great service, I recommend it!\nDisappointed by the delivery…\nNothing to report, fine.',
    topic: 'Topic',
    groupsThePointsForTheTime: 'Groups the points for the time series',
    platform: 'Platform',
    none: 'None',
    persist: 'Persist',
    classifying: 'Classifying…',
    classify: 'Classify',
    textSClassifiedPersistedPersisted: 'text(s) classified · {{persisted}} persisted',
    countTextSClassified: '{{count}} text(s) classified',
    netSentiment: 'Net sentiment',
    overallOverall: 'Overall: {{overall}}',
    positive2: 'Positive',
    neutral2: 'Neutral',
    negative2: 'Negative',
    timeSeriesUnavailable: 'Time series unavailable.',
    sentimentTrend: 'Sentiment trend',
    all: 'All',
    all2: 'All',
    granularity: 'Granularity',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    show: 'Show',
    noSentimentPoint: 'No sentiment point',
    classifyTextsWithAnalyzePersistAbove: 'Classify texts with “Analyze & persist” above (or ingest sentiment points) to build the series.',
    netSentimentPer: 'Net sentiment (%) per',
    classifiedVolume: 'Classified volume',
    sentiment: 'Sentiment',
    aiSentimentClassificationCreditedOnYour: 'AI sentiment classification (credited) on your texts, with a net aggregate and a trend over time. The classification fails cleanly if AI is unavailable — never a simulated label.',
  },
  shareOfVoicePage: {
    myBrand: 'My brand',
    couldNotComputeTheShareOf: 'Could not compute the share of voice.',
    entity: 'Entity',
    shareOfVoice: 'Share of voice',
    compareYourPresenceWithYourCompetitors: 'Compare your presence with your competitors\' on a metric. Enter the values, or use your tracked competitors and your own value.',
    metric: 'Metric',
    competitorSource: 'Competitor source',
    manualEntry: 'Manual entry',
    trackedCompetitors: 'Tracked competitors',
    theValuesComeFromTheSaved: 'The values come from the saved metrics of your tracked competitors for “',
    syncThemFirstCompetitorsTabSync: '”. Sync them first (Competitors tab → “Sync”, which collects',
    followers: 'followers',
    engagement: 'engagement',
    views: 'views',
    competitorsWithoutAMetricForThis: '); competitors without a metric for this value are ignored. Add your own value below to include yourself.',
    entities: 'Entities',
    competitor: 'Competitor',
    value: 'value',
    remove: 'Remove',
    addAnEntity: '+ Add an entity',
    yourLabel: 'Your label',
    yourValue: 'Your value',
    optional: 'Optional',
    eG800: 'e.g. 800',
    computing: 'Computing…',
    computeTheShareOfVoice: 'Compute the share of voice',
    result: 'Result',
    computingTheShareOfVoice: 'Computing the share of voice…',
    insufficientData: 'Insufficient data',
    enterAtLeastOneEntityWith: 'Enter at least one entity with a value (or enable tracked competitors with saved metrics), and/or your own value.',
    leader: 'Leader',
    totalMetric: 'Total {{metric}}',
    noComputationYet: 'No computation yet',
    fillInYourEntitiesThenRun: 'Fill in your entities, then run the computation to see the breakdown.',
  },
  teamProductivityPage: {
    reportUnavailable: 'Report unavailable.',
    teamProductivity: 'Team productivity',
    postsActuallyPublishedPerAuthorPlatform: 'Posts actually published per author/platform + inbox responsiveness (first reply / resolution rate). Two real halves — no made-up figures.',
    byAuthor: 'By author',
    byPlatform: 'By platform',
    publishedPosts: 'Published posts',
    conversationsHandled: 'Conversations handled',
    replyRate: 'Reply rate',
    resolutionRate: 'Resolution rate',
    noActivity: 'No activity',
    noPublishedPostOrInboxEvent: 'No published post or inbox event in the period.',
    byPlatform2: 'by platform',
    byAuthor2: 'by author',
    noPublishedPost: 'No published post.',
    inboxResponsivenessPerAgent: 'Inbox responsiveness per agent',
    noInboxEventIngested: 'No inbox event ingested.',
    agent: 'Agent',
    processed: 'Processed',
    replyRate2: 'Reply rate',
    n1stReply: '1st reply',
    resolution: 'Resolution',
    unassigned: '(unassigned)',
    detailByAuthor: 'Detail by author',
    author: 'Author',
    platforms: 'Platforms',
    first: 'First',
    last: 'Last',
    unknown: '(unknown)',
  },
  trendsPage: {
    available: 'available',
    unavailable: 'unavailable',
    active: 'active',
    inactive: 'inactive',
    n7Days: '7 days',
    n30Days: '30 days',
    n90Days: '90 days',
    n12Weeks: '12 weeks',
    new: 'New',
    rising: 'Rising',
    falling: 'Falling',
    stable: 'Stable',
    positive: 'Positive',
    negative: 'Negative',
    neutral: 'Neutral',
    trendDetectionUnavailable: 'Trend detection unavailable.',
    detectedTrends: 'Detected trends',
    period: 'Period',
    platform: 'Platform',
    all: 'All',
    detect: 'Detect',
    noTrendInThePeriod: 'No trend in the period',
    risingHashtagsComeFromYourIngested: 'Rising hashtags come from your ingested hashtag metrics; spiking topics come from your listening monitors. Add hashtags to track or configure listening to feed the detection.',
    risingHashtagsByMentionVolume: 'Rising hashtags (by mention volume)',
    hashtagDetail: 'Hashtag detail',
    vMentions: '{{v}} mentions',
    spikingListeningTopics: 'Spiking listening topics',
    spike: 'Spike ⚡',
    normal: 'Normal',
    recentBaselineBaselineAvg: 'recent · baseline {{baseline_avg}}',
    mentionsUnavailable: 'Mentions unavailable.',
    mentions: 'Mentions',
    searchEnablesWebRssListening: 'Search (enables web/RSS listening)',
    keywordOrBooleanQuery: 'Keyword or boolean query',
    myBrand: 'my brand',
    load: 'Load',
    ownedChannels: 'Owned channels',
    webRssListening: 'Web/RSS listening',
    noMentions: 'No mentions',
    mentionsComeFromYourOwnedChannels: 'Mentions come from your owned channels (received comments/DMs) and, if a search is entered and listening is configured, from licensed RSS/web sources.',
    owned: 'Owned',
    anonymous: 'Anonymous',
    openTheSource: 'Open the source ↗',
    trendsMentions: 'Trends & mentions',
    trendDetectionRisingHashtagsSpikingListe: 'Trend detection (rising hashtags + spiking listening topics) and aggregation of mentions across your owned channels and licensed web/RSS sources. No data is fabricated.',
  },
  visionPage: {
    couldNotReadTheFile: 'Could not read the file.',
    insufficientAiCredits: 'Insufficient AI credits',
    topUpTheBalance: 'Top up the balance',
    visionAiTemporarilyUnavailable: 'Vision AI temporarily unavailable',
    contactAddedToTheCrm: 'Contact added to the CRM.',
    couldNotAddToTheCrm: 'Could not add to the CRM (permissions required?).',
    company: 'Company',
    phones: 'Phones',
    website: 'Website',
    networks: 'Networks',
    noReadableField: 'No readable field',
    theModelCouldNotReadAny: 'The model could not read any usable field in this image.',
    adding: 'Adding…',
    addToTheCrm: 'Add to the CRM',
    viewTheCreatedContact: 'View the created contact →',
    imageTooLargeMax8Mb: 'Image too large (max 8 MB).',
    chooseAnImageFileOrUrl: 'Choose an image (file or URL) first.',
    theJsonSchemaIsInvalid: 'The JSON schema is invalid.',
    theImageAnalysisFailed: 'The image analysis failed.',
    visionOcr: 'Vision / OCR',
    extractStructuredFieldsFromAnImage: 'Extract structured fields from an image, or scan a business card and add it to the CRM. AI processing billed at actual cost — honest states when unavailable.',
    businessCard: 'Business card',
    structuredExtraction: 'Structured extraction',
    imageFile: 'Image (file)',
    pngJpgMax8Mb: 'PNG / JPG, max 8 MB',
    orAUrl: 'or a URL',
    imageUrl: 'Image URL',
    use: 'Use',
    fieldSchemaJsonOptional: 'Field schema (JSON, optional)',
    eGInvoiceNumberStringTotal: 'e.g. {"invoice_number":"string","total":"string"}',
    fieldDescription: '{"field": "description"}',
    invalidJson: 'Invalid JSON.',
    preview: 'Preview',
    analyzing: 'Analyzing…',
    scanTheCard: 'Scan the card',
    extractTheFields: 'Extract the fields',
    result: 'Result',
    analyzingTheImage: 'Analyzing the image…',
    noResults: 'No results',
    chooseAnImageThenRunThe: 'Choose an image, then run the analysis to see the extracted data.',
    name: 'Name',
    function: 'Function',
    address: 'Address',
  },
  webTrafficPage: {
    direct: '(direct)',
    trafficAnalyticsUnavailable: 'Traffic analytics unavailable.',
    webTraffic: 'Web traffic',
    firstPartyAnalyticsFromYourVisitor: 'First-party analytics from your visitor pixel, with UTM attribution by medium (utm_medium) and by campaign (utm_campaign). Real, persisted data.',
    siteIdOptional: 'site_id (optional)',
    filterBySite: 'Filter by site',
    pageViews: 'Page views',
    uniqueVisitors: 'Unique visitors',
    sessions: 'Sessions',
    identificationRate: 'Identification rate',
    vIdentifiedV2Anonymous: '{{v}} identified / {{v2}} anonymous',
    noTraffic: 'No traffic',
    theVisitorPixelIsNotConfigured: 'The visitor pixel is not configured, or no event has been collected yet.',
    mediumsUtmMedium: 'Mediums (utm_medium)',
    noUtmMediumParameterObserved: 'No utm_medium parameter observed.',
    campaignsUtmCampaign: 'Campaigns (utm_campaign)',
    noUtmCampaignParameterObserved: 'No utm_campaign parameter observed.',
    sources: 'Sources',
    noSource: 'No source.',
    mostViewedPages: 'Most viewed pages',
    noPage: 'No page.',
  },
  goalsOverviewWidget: {
    estimated: '(est.)',
    progressOfName: 'Progress of {{name}}',
    dueDate: 'Due: {{date}}',
    today: 'today',
    daysLeft: 'D-{{days}}',
    goalsUnavailable: 'Goals unavailable.',
    noRunningGoal: 'No running goal',
    noGoalDefined: 'No goal defined',
    finishedGoalsDefineANewOne: '{{count}} goal(s) finished. Define a new goal to track your progress.',
    defineAGoalToTrackProgress: 'Define a goal (KPI, OKR) to track your progress here.',
    moreRunningGoals: '+{{count}} more running goal(s)',
    createAGoal: 'Create a goal →',
    manageGoals: 'Manage goals →',
    goals: 'Goals',
    runningGoalsHeading: '🎯 Running goals',
  },
};

export default en;
